import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, CreditCard, Check, ChevronLeft, Loader2, Copy, Upload, Tag, X, Mail } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

/**
 * Paywall localisé par pays :
 *  1) Choix du mode de paiement (prix CACHÉS tant qu'aucun mode n'est choisi)
 *  2) Une fois le mode choisi → prix affiché dans la devise du pays de l'utilisateur
 *     (repli USD si le pays n'est pas configuré). Carte = Stripe (USD).
 *  Modes possibles : M-PESA, Orange, MTN, virement, carte, e-Transfert.
 */
const METHOD_META = {
  mpesa:     { label: 'M-PESA',            icon: '🟢', sub: 'Mobile money' },
  orange:    { label: 'Orange Money',      icon: '🟠', sub: 'Mobile money' },
  mtn:       { label: 'MTN Money',         icon: '🟡', sub: 'Mobile money' },
  bank:      { label: 'Virement bancaire', icon: '🏦', sub: 'International' },
  card:      { label: 'Carte bancaire',    icon: '💳', sub: 'Visa / Mastercard — USD' },
  etransfer: { label: 'e-Transfert (Interac)', icon: '✉️', sub: 'Virement Interac par email' },
};

export default function PaywallScreen({ moduleInfo, pricing, token, onBack }) {
  const [method, setMethod] = useState(null);
  const [settings, setSettings] = useState({});
  const [local, setLocal] = useState(null); // {currency, amount, usd_amount, methods, description}
  const [txRef, setTxRef] = useState('');
  const [note, setNote] = useState('');
  const [proofImage, setProofImage] = useState('');
  const [proofPreview, setProofPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedPayment, setSubmittedPayment] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);

  useEffect(() => {
    apiCall('/payment-settings/public').then(setSettings).catch(() => {});
    apiCall('/my-pricing', 'GET', null, token).then(r => {
      const it = (r.items || []).find(x => x.module_id === moduleInfo.id);
      setLocal(it || null);
    }).catch(() => {});
  }, [moduleInfo.id, token]);

  const pct = promoApplied?.discount_pct || 0;
  const currency = local?.currency || 'USD';
  const baseAmount = local?.amount ?? pricing?.price_usd;
  const usdAmount = local?.usd_amount ?? pricing?.price_usd;
  const amount = baseAmount != null ? Math.round(baseAmount * (1 - pct / 100) * 100) / 100 : null;
  const usd = usdAmount != null ? Math.round(usdAmount * (1 - pct / 100) * 100) / 100 : null;

  const availableMethods = (local?.methods && local.methods.length) ? local.methods : ['mpesa', 'orange', 'mtn', 'bank', 'card', 'etransfer'];
  const isCard = method === 'card';
  const payAmount = isCard ? usd : amount;
  const payCurrency = isCard ? 'USD' : currency;
  const fmt = (a, c) => `${Number(a).toLocaleString('fr-FR')} ${c}`;

  const handleProofUpload = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error('Image trop grande (max 3 MB)');
    const reader = new FileReader();
    reader.onload = (e) => { setProofImage(e.target.result); setProofPreview(e.target.result); };
    reader.readAsDataURL(file);
  };

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    try {
      const r = await apiCall('/promo/check', 'POST', { code: promoInput.trim().toUpperCase(), module_id: moduleInfo.id }, token);
      setPromoApplied(r);
      toast.success(`Code appliqué : -${r.discount_pct}%`);
    } catch (e) { toast.error(e.message); setPromoApplied(null); }
  };
  const removePromo = () => { setPromoApplied(null); setPromoInput(''); };

  const submitManual = async () => {
    if (!txRef.trim()) return toast.error('Code / référence de transaction requis');
    if (!payAmount) return toast.error('Prix non configuré');
    setSubmitting(true);
    try {
      const r = await apiCall('/payments/manual', 'POST', {
        module_id: moduleInfo.id, method,
        transaction_ref: txRef.trim(), amount: payAmount, currency: payCurrency,
        note, proof_image: proofImage, promo_code: promoApplied?.code || '',
      }, token);
      setSubmittedPayment(r);
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  const startStripe = async () => {
    if (!usd) return toast.error('Prix USD non configuré');
    setStripeLoading(true);
    try {
      const r = await apiCall('/payments/stripe/checkout', 'POST', {
        module_id: moduleInfo.id, currency: 'usd', origin_url: window.location.origin, promo_code: promoApplied?.code || '',
      }, token);
      window.location.href = r.url;
    } catch (e) { toast.error(e.message); setStripeLoading(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copié !'); };

  if (submittedPayment) {
    return <PaymentPendingPoll paymentId={submittedPayment.payment_id} moduleId={moduleInfo.id} token={token} onBack={onBack} />;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto px-4 py-8" data-testid="paywall-screen">
      <button onClick={method ? () => setMethod(null) : onBack} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />{method ? 'Changer de mode de paiement' : 'Retour'}</button>

      <div className="bg-gradient-to-br from-yellow-500/15 to-emerald-500/15 border-2 border-yellow-400/30 rounded-3xl p-6 mb-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center"><Lock size={24} className="text-slate-950" /></div>
          <div>
            <div className="text-xs uppercase tracking-wider text-yellow-300">Accès complet</div>
            <h2 className="text-2xl font-bold">{moduleInfo?.title_fr || moduleInfo?.title}</h2>
          </div>
        </div>
        <p className="text-white/70 text-sm">{local?.description || pricing?.description || 'Débloque ce module pour suivre toutes ses leçons à vie.'}</p>
      </div>

      <AnimatePresence mode="wait">
        {!method && (
          <motion.div key="choose" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="paywall-choose-method">
            <h3 className="text-sm font-bold text-white/80 mb-3">Choisis ton mode de paiement pour voir le tarif adapté à ta devise :</h3>
            <div className="space-y-2">
              {availableMethods.map(id => {
                const m = METHOD_META[id]; if (!m) return null;
                return (
                  <button key={id} onClick={() => setMethod(id)} data-testid={`pay-method-${id}`}
                    className="w-full text-left bg-white/5 hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-400/40 rounded-xl p-4 flex items-center gap-4 transition">
                    <span className="text-3xl">{m.icon}</span>
                    <div className="flex-1"><div className="font-bold">{m.label}</div><div className="text-xs text-white/50">{m.sub}</div></div>
                    <ChevronLeft size={16} className="text-emerald-300 rotate-180" />
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {method && (
          <motion.div key="pay" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div className="bg-yellow-500/10 border border-yellow-400/30 rounded-2xl p-4 mb-4" data-testid="paywall-price">
              <div className="text-xs text-yellow-300 uppercase tracking-wider mb-1">Tarif {METHOD_META[method]?.label}</div>
              <div className="flex flex-wrap items-center gap-3">
                {payAmount ? <span className="text-2xl font-black text-yellow-200">{fmt(payAmount, payCurrency)}</span>
                  : <span className="text-amber-300 text-sm">⚠️ Tarif non configuré pour ce mode</span>}
                {promoApplied && <span className="bg-emerald-500/20 text-emerald-300 text-xs font-bold px-2 py-1 rounded-full">-{promoApplied.discount_pct}% appliqué</span>}
              </div>
            </div>

            {/* Code promo */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4">
              <label className="text-xs text-emerald-300 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Tag size={12} />Code promo (optionnel)</label>
              {promoApplied ? (
                <div className="flex items-center justify-between bg-emerald-500/15 rounded-lg p-2">
                  <div><div className="font-bold text-emerald-200">{promoApplied.code} · -{promoApplied.discount_pct}%</div>{promoApplied.description && <div className="text-xs text-white/60">{promoApplied.description}</div>}</div>
                  <button onClick={removePromo} className="text-red-300 hover:text-red-200" data-testid="remove-promo"><X size={16} /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} placeholder="Ex: BIENVENUE10" className="flex-1 bg-slate-900 border border-white/10 rounded-lg p-2 text-sm" data-testid="promo-input" />
                  <button onClick={applyPromo} disabled={!promoInput.trim()} data-testid="apply-promo" className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold px-4 py-2 rounded-lg text-sm">Appliquer</button>
                </div>
              )}
            </div>

            {/* Modes manuels : mobile money + virement */}
            {['mpesa', 'orange', 'mtn', 'bank'].includes(method) && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                <div className="text-xs text-emerald-300 uppercase tracking-wider">Effectue ton paiement vers</div>
                {method === 'mpesa' && <PayInfoCard label="Numéro M-PESA" value={settings.mpesa_number} name={settings.mpesa_name} onCopy={copy} />}
                {method === 'orange' && <PayInfoCard label="Numéro Orange Money" value={settings.orange_number} name={settings.orange_name} onCopy={copy} />}
                {method === 'mtn' && <PayInfoCard label="Numéro MTN Money" value={settings.mtn_number} name={settings.mtn_name} onCopy={copy} />}
                {method === 'bank' && (
                  <div className="space-y-2 text-sm">
                    <PayInfoCard label="Banque" value={settings.bank_name} onCopy={copy} />
                    <PayInfoCard label="Numéro de compte / IBAN" value={settings.bank_account} onCopy={copy} />
                    <PayInfoCard label="Titulaire" value={settings.bank_holder} onCopy={copy} />
                    {settings.bank_swift && <PayInfoCard label="SWIFT/BIC" value={settings.bank_swift} onCopy={copy} />}
                  </div>
                )}
                {settings.instructions && <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-xs text-emerald-200">💡 {settings.instructions}</div>}
                <ManualDeclare {...{ txRef, setTxRef, note, setNote, proofPreview, handleProofUpload, submitManual, submitting, payAmount, payCurrency, fmt }} />
              </div>
            )}

            {/* e-Transfert */}
            {method === 'etransfer' && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                <div className="text-xs text-emerald-300 uppercase tracking-wider flex items-center gap-1.5"><Mail size={12} />Paiement par e-Transfert (Interac)</div>
                <PayInfoCard label="Adresse e-Transfert" value={settings.etransfer_email || 'support.josmarttech@gmail.com'} onCopy={copy} />
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-xs text-emerald-200">
                  {settings.etransfer_instructions || "Envoyez le virement Interac e-Transfer à l'adresse ci-dessus, puis envoyez la capture d'écran à cette même adresse. Validation sous 1 à 30 minutes."}
                </div>
                <ManualDeclare {...{ txRef, setTxRef, note, setNote, proofPreview, handleProofUpload, submitManual, submitting, payAmount, payCurrency, fmt }} />
              </div>
            )}

            {/* Carte — Stripe */}
            {method === 'card' && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
                <CreditCard size={36} className="mx-auto mb-3 text-emerald-300" />
                <h4 className="font-bold">Paiement par carte (Stripe)</h4>
                <p className="text-sm text-white/60 mt-1">Visa, Mastercard, Amex — paiement sécurisé via Stripe (facturé en USD).</p>
                <button onClick={startStripe} disabled={stripeLoading || !usd} data-testid="start-stripe"
                  className="mt-4 w-full bg-gradient-to-r from-purple-500 to-emerald-500 hover:from-purple-400 hover:to-emerald-400 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                  {stripeLoading ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                  Payer {usd ? fmt(usd, 'USD') : '...'} par carte
                </button>
                {!usd && <div className="text-xs text-amber-300 mt-2">Prix USD non configuré.</div>}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ManualDeclare({ txRef, setTxRef, note, setNote, proofPreview, handleProofUpload, submitManual, submitting, payAmount, payCurrency, fmt }) {
  return (
    <div className="border-t border-white/10 pt-4">
      <div className="text-xs text-emerald-300 uppercase tracking-wider mb-2">Après ton paiement, déclare-le ici</div>
      <input value={txRef} onChange={e => setTxRef(e.target.value)} placeholder="Code / référence de transaction" className="w-full bg-slate-900 border border-white/10 rounded-lg p-2.5 text-sm" data-testid="tx-ref-input" />
      <label className="block mt-3" data-testid="proof-upload-area">
        <div className={`bg-slate-900 border-2 border-dashed rounded-lg p-3 text-center text-xs cursor-pointer hover:border-emerald-400/50 ${proofPreview ? 'border-emerald-400/40' : 'border-white/15 text-white/50'}`}>
          {proofPreview ? (
            <div className="flex items-center gap-2 justify-center"><img src={proofPreview} alt="preuve" className="h-16 rounded" /><span className="text-emerald-300">Preuve attachée ✓</span></div>
          ) : (
            <div className="flex items-center gap-2 justify-center"><Upload size={14} /><span>Joindre une capture d'écran (recommandé)</span></div>
          )}
        </div>
        <input type="file" accept="image/*" className="hidden" onChange={e => handleProofUpload(e.target.files?.[0])} data-testid="proof-input" />
      </label>
      <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note optionnelle (ton nom, date du paiement)" className="w-full bg-slate-900 border border-white/10 rounded-lg p-2.5 text-sm mt-3" rows={2} data-testid="tx-note-input" />
      <button onClick={submitManual} disabled={submitting || !txRef || !payAmount} data-testid="submit-manual"
        className="mt-3 w-full bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 text-slate-950 font-bold py-2.5 rounded-lg flex items-center justify-center gap-2">
        {submitting ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}J'ai payé {payAmount ? fmt(payAmount, payCurrency) : ''}
      </button>
    </div>
  );
}

function PayInfoCard({ label, value, name, onCopy }) {
  if (!value) return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-200 text-xs">⚠️ {label} pas encore configuré par l'admin</div>
  );
  return (
    <div className="flex items-center justify-between bg-slate-900/60 rounded-lg p-3">
      <div className="min-w-0">
        <div className="text-xs text-white/50">{label}</div>
        <div className="font-mono font-bold text-emerald-200 truncate">{value}</div>
        {name && <div className="text-xs text-white/50">au nom de {name}</div>}
      </div>
      <button onClick={() => onCopy(value)} className="bg-emerald-500/20 hover:bg-emerald-500/30 p-2 rounded-lg flex-shrink-0"><Copy size={14} className="text-emerald-300" /></button>
    </div>
  );
}

function PaymentPendingPoll({ paymentId, moduleId, token, onBack }) {
  const [validated, setValidated] = useState(false);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const access = await apiCall('/my-access', 'GET', null, token);
        if (access?.[moduleId]) { if (!stop) { setValidated(true); toast.success('🎉 Paiement validé ! Accès débloqué.'); } }
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => { stop = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [moduleId, token]);

  if (validated) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="paywall-validated">
        <div className="text-7xl mb-3">🎉</div>
        <h2 className="text-3xl font-black text-emerald-300">Paiement validé !</h2>
        <p className="text-white/70 mt-2">Ton accès au module est débloqué. Bonne formation !</p>
        <button onClick={onBack} className="mt-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-3 rounded-xl" data-testid="back-after-validation">Commencer</button>
      </motion.div>
    );
  }
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="paywall-submitted">
      <div className="text-6xl mb-3">⏳</div>
      <h2 className="text-2xl font-bold">Paiement en attente de validation</h2>
      <p className="text-white/60 mt-2 text-sm">L'admin vérifiera ton paiement (généralement sous 1 à 30 minutes). Cette page se met à jour automatiquement dès que ton accès est débloqué.</p>
      <div className="flex items-center justify-center gap-2 mt-3 text-xs text-emerald-300/70"><Loader2 size={12} className="animate-spin" />Vérification toutes les 20s</div>
      <div className="bg-white/5 border border-white/10 rounded-xl p-3 mt-4 text-xs"><div className="text-white/50">Référence du dossier</div><div className="font-mono">{paymentId}</div></div>
      <button onClick={onBack} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold px-6 py-3 rounded-xl">Retour</button>
    </motion.div>
  );
}
